"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ChevronDown } from "lucide-react";
import { LogoMark } from "./Logo";
import { createClient } from "@/lib/supabase";
import { resetAnalyticsIdentity } from "@/lib/analytics";
import type { UserRole } from "@/lib/auth";
import { CrewUnreadDot } from "@/app/components/CrewUnreadDot";
import type { NavLink } from "./nav-model";
import {
  ABOUT_NAV_LINKS,
  ABOUT_PEAKWAVE_NAV_LINKS,
  candidateAccountLinks,
  MORE_NAV_LINKS,
  NAV_VISIBILITY_CLASSES,
  RESOURCE_NAV_LINKS,
  SECONDARY_NAV_LINKS,
  companionAccountBadgeLabels,
  isNavLinkActive,
  primaryNavLinksFor,
} from "./nav-model";

export function CompanionAccountBadge({ label }: { label: "Call" | "Conditions" }) {
  const accessibleLabel =
    label === "Call" ? "Unread Companion Call" : "Strong saved conditions signal";

  return (
    <span
      role="note"
      aria-label={accessibleLabel}
      className={`ml-1.5 inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none ${
        label === "Call" ? "bg-gold/20 text-navy" : "bg-emerald-100 text-emerald-800"
      }`}
    >
      {label}
    </span>
  );
}

export function Navbar({
  userRole,
  authenticated = false,
  unreadCount = 0,
  shortlistCount = 0,
  candidateName,
  candidatePhotoUrl,
  profileId,
  employerCompanyName,
  employerCompanyLogo,
  employerCompanySlug,
  crewAnyUnread = false,
  crewAnyMention = false,
  hasEpicSpot = false,
  callsUnread = false,
  workProfileVisible = false,
}: {
  userRole?: UserRole;
  authenticated?: boolean;
  unreadCount?: number;
  shortlistCount?: number;
  candidateName?: string;
  candidatePhotoUrl?: string;
  profileId?: string;
  employerCompanyName?: string;
  employerCompanyLogo?: string;
  employerCompanySlug?: string;
  crewAnyUnread?: boolean;
  crewAnyMention?: boolean;
  hasEpicSpot?: boolean;
  callsUnread?: boolean;
  workProfileVisible?: boolean;
}) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [employerDropdownOpen, setEmployerDropdownOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const employerRef = useRef<HTMLDivElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const previousScrollY = useRef(0);
  const [mobileCompanionHeaderHidden, setMobileCompanionHeaderHidden] =
    useState(false);

  const isLoggedIn = authenticated || userRole === "employer" || userRole === "candidate";
  const companionOnly = isLoggedIn && !userRole;
  const personalAccount = userRole === "candidate" || companionOnly;
  const inboxPath = userRole === "employer" ? "/employer/messages" : companionOnly ? "/companion/notifications" : "/inbox";
  const companionAccountBadges = companionAccountBadgeLabels({ callsUnread, hasEpicSpot });
  const primaryNavLinks = primaryNavLinksFor(companionOnly ? "candidate" : userRole);
  const accountLinks = companionOnly ? [
    { id: "companion", href: "/companion/you", label: "Your Companion" },
    { id: "settings", href: "/settings", label: "Account settings" },
    { id: "data", href: "/companion/you/data", label: "Your data" },
  ] : candidateAccountLinks({
    profileId,
    workProfileVisible,
  });

  const MORE_ROUTES = MORE_NAV_LINKS.map((link) => link.href);
  const isMoreActive = MORE_ROUTES.some((r) => pathname === r || pathname.startsWith(r + "/"));

  function navLinkClass(href: string, base: string) {
    const active = isNavLinkActive(pathname, href);
    return `${base} ${active ? "text-navy font-semibold bg-navy/[0.06]" : "text-text-body hover:text-navy hover:bg-navy/[0.04]"}`;
  }

  function mobileNavLinkClass(href: string) {
    const active = isNavLinkActive(pathname, href);
    return `block min-h-[44px] px-4 py-3.5 text-[15px] font-medium no-underline rounded-sm active:bg-warm-bg ${active ? "text-navy font-semibold" : "text-text-body"}`;
  }

  function mobilePrimaryLinkClass(href: string) {
    const active = isNavLinkActive(pathname, href);
    return `flex min-h-[54px] items-center justify-between rounded-[14px] border px-4 py-3 text-[15px] font-semibold no-underline transition-colors ${
      active
        ? "border-teal/25 bg-teal/10 text-navy"
        : "border-line bg-white text-navy active:bg-cream"
    }`;
  }

  useEffect(() => {
    function handleScroll() {
      const nextScrollY = window.scrollY;
      setScrolled(nextScrollY > 10);
      if (
        pathname.startsWith("/companion") &&
        window.innerWidth < 1120 &&
        !mobileOpen
      ) {
        setMobileCompanionHeaderHidden(
          nextScrollY > previousScrollY.current && nextScrollY > 80,
        );
      } else {
        setMobileCompanionHeaderHidden(false);
      }
      previousScrollY.current = nextScrollY;
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mobileOpen, pathname]);

  // Close desktop dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
      if (employerRef.current && !employerRef.current.contains(e.target as Node)) {
        setEmployerDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lock body scroll when mobile nav is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Close mobile nav on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    function handleEscape(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        setMobileOpen(false);
        window.requestAnimationFrame(() => mobileToggleRef.current?.focus());
      }
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) setMobileMoreOpen(false);
  }, [mobileOpen]);

  async function handleLogout() {
    const { clearOfflineAccount } = await import("@/lib/offline/context");
    await clearOfflineAccount();
    const { clearNativeWidgetSnapshot } = await import("@/lib/native/widget-bridge");
    await clearNativeWidgetSnapshot();
    const supabase = createClient();
    await supabase.auth.signOut();
    resetAnalyticsIdentity();
    // Hard navigation to clear server-side cookie state
    window.location.href = "/";
  }

  const inboxBadge = unreadCount > 0 ? (
    <span className="w-[18px] h-[18px] bg-gold text-white rounded-full text-[10px] font-bold inline-flex items-center justify-center ml-1">
      {unreadCount > 9 ? "9+" : unreadCount}
    </span>
  ) : null;

  const chevronSvg = (open: boolean) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  function desktopMoreLinkClass(link: NavLink, nested = false) {
    const layout = link.id === "community" ? "flex items-center gap-1.5" : "block";
    const indent = nested ? "pl-6 text-text-muted" : "";
    return `${layout} px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy ${indent}`;
  }

  function mobileMoreLinkClass(link: NavLink, nested = false) {
    const gap = link.id === "community" ? "gap-1.5" : "";
    const xPadding = nested ? "pl-8 pr-4" : "px-4";
    return `flex min-h-[44px] items-center ${gap} ${xPadding} py-[11px] text-sm font-medium text-text-muted no-underline rounded-sm`;
  }

  function renderDesktopMoreLink(link: NavLink, nested = false) {
    return (
      <Link
        key={link.href}
        href={link.href}
        className={desktopMoreLinkClass(link, nested)}
        onClick={() => setMoreOpen(false)}
      >
        {link.label}
        {link.badge ? (
          <span className="ml-1.5 rounded-full border border-gold/35 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.08em] text-gold-dark">
            {link.badge}
          </span>
        ) : null}
        {link.id === "community" ? (
          <CrewUnreadDot hasUnread={crewAnyUnread} hasMention={crewAnyMention} />
        ) : null}
      </Link>
    );
  }

  function renderMobileMoreLink(link: NavLink, nested = false) {
    return (
      <Link
        key={link.href}
        href={link.href}
        className={mobileMoreLinkClass(link, nested)}
        onClick={() => setMobileOpen(false)}
      >
        {link.label}
        {link.badge ? (
          <span className="ml-1.5 rounded-full border border-gold/35 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.08em] text-gold-dark">
            {link.badge}
          </span>
        ) : null}
        {link.id === "community" ? (
          <CrewUnreadDot hasUnread={crewAnyUnread} hasMention={crewAnyMention} />
        ) : null}
      </Link>
    );
  }

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[1000] flex h-16 items-center justify-between gap-4 px-5 md:px-10 bg-warm-bg/92 backdrop-blur-[20px] backdrop-saturate-[1.4] border-b border-navy/6 transition-[box-shadow,transform] duration-300 min-[1120px]:translate-y-0 ${
          mobileCompanionHeaderHidden ? "-translate-y-full" : "translate-y-0"
        } ${
          scrolled ? "shadow-[0_2px_24px_rgba(0,0,0,0.06)]" : ""
        }`}
      >
        {/* Brand */}
        <Link href="/" onClick={() => setMobileOpen(false)} className="z-10 flex min-h-[44px] items-center gap-2.5 no-underline shrink-0">
          <LogoMark />
          <span className="font-sans text-[28px] font-bold text-navy tracking-normal">
            PeakWave
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className={`${NAV_VISIBILITY_CLASSES.desktop} absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center gap-1.5`}>
          {primaryNavLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={navLinkClass(
                link.href,
                `${
                  link.id === "community" ? "inline-flex items-center gap-1.5" : ""
                } min-h-[44px] whitespace-nowrap text-[15px] font-semibold px-4 py-2.5 rounded-sm transition-all tracking-normal no-underline inline-flex items-center`,
              )}
            >
              {link.label}
              {link.id === "companion" && userRole === "candidate"
                ? companionAccountBadges.map((label) => (
                    <CompanionAccountBadge key={label} label={label} />
                  ))
                : null}
              {link.id === "community" ? (
                <CrewUnreadDot hasUnread={crewAnyUnread} hasMention={crewAnyMention} />
              ) : null}
            </Link>
          ))}

          {/* More Dropdown */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              aria-expanded={moreOpen}
              aria-haspopup="true"
              aria-controls="desktop-more-menu"
              className={`flex min-h-[44px] items-center gap-[5px] whitespace-nowrap text-[15px] font-semibold px-4 py-2.5 rounded-sm transition-all tracking-normal bg-transparent border-none cursor-pointer font-sans ${isMoreActive ? "text-navy bg-navy/[0.06]" : "text-text-body hover:text-navy hover:bg-navy/[0.04]"}`}
            >
              More
              {chevronSvg(moreOpen)}
            </button>
            {moreOpen && <div
              id="desktop-more-menu"
              className="absolute top-[calc(100%+8px)] right-0 bg-white rounded-md shadow-[0_12px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.04)] p-2 min-w-[200px] transition-all duration-200 opacity-100 pointer-events-auto translate-y-0"
            >
              <span className="block px-3.5 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted/60">
                Explore
              </span>
              {SECONDARY_NAV_LINKS.map((link) => renderDesktopMoreLink(link))}
              <div className="h-px bg-border-light mx-1 my-1.5" />
              <span className="block px-3.5 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted/60">
                Resources
              </span>
              {RESOURCE_NAV_LINKS.map((link) => renderDesktopMoreLink(link))}
              <div className="h-px bg-border-light mx-1 my-1.5" />
              <span className="block px-3.5 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted/60">
                About
              </span>
              {ABOUT_NAV_LINKS.map((link) => renderDesktopMoreLink(link))}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between rounded-sm px-3.5 py-2.5 text-[13px] font-medium text-text-body transition-all hover:bg-warm-bg hover:text-navy [&::-webkit-details-marker]:hidden">
                  <span>About PeakWave</span>
                  <ChevronDown
                    aria-hidden="true"
                    className="h-3.5 w-3.5 text-text-muted transition-transform group-open:rotate-180"
                  />
                </summary>
                <div className="mt-0.5">
                  {ABOUT_PEAKWAVE_NAV_LINKS.map((link) => renderDesktopMoreLink(link, true))}
                </div>
              </details>
            </div>}
          </div>
        </div>

        <div className={`${NAV_VISIBILITY_CLASSES.desktop} z-10 ml-auto items-center justify-end gap-1.5`}>
          {/* Inbox icon (logged in only) */}
          {isLoggedIn && (
            <Link
              href={inboxPath}
              className="relative min-h-[44px] min-w-[44px] p-2 rounded-sm transition-all hover:bg-navy/[0.04] no-underline inline-flex items-center justify-center"
              aria-label="Inbox"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-text-body"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-[16px] h-[16px] bg-gold text-white rounded-full text-[9px] font-bold flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Link>
          )}

          {/* Employer company dropdown */}
          {userRole === "employer" && (
            <div className="relative" ref={employerRef}>
              <button
                onClick={() => setEmployerDropdownOpen(!employerDropdownOpen)}
                aria-expanded={employerDropdownOpen}
                aria-haspopup="true"
                aria-controls="desktop-employer-menu"
                className="flex min-h-[44px] items-center gap-2 text-[13px] font-medium text-text-body px-3.5 py-2 rounded-sm transition-all hover:text-navy hover:bg-navy/[0.04] tracking-[0.2px] bg-transparent border-none cursor-pointer font-sans"
              >
                {employerCompanyLogo ? (
                  <Image
                    src={employerCompanyLogo}
                    alt=""
                    width={24}
                    height={24}
                    sizes="24px"
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <span aria-hidden="true" className="w-6 h-6 rounded-full bg-navy text-white text-[10px] font-bold flex items-center justify-center">
                    {employerCompanyName?.charAt(0).toUpperCase() ?? "C"}
                  </span>
                )}
                {employerCompanyName ?? "Company"}
                {chevronSvg(employerDropdownOpen)}
              </button>
              {employerDropdownOpen && <div
                id="desktop-employer-menu"
                className="absolute top-[calc(100%+8px)] right-0 bg-white rounded-md shadow-[0_12px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.04)] p-2 min-w-[200px] transition-all duration-200 opacity-100 pointer-events-auto translate-y-0"
              >
                <Link
                  href="/employer/dashboard"
                  className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy"
                  onClick={() => setEmployerDropdownOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  href="/employer/company"
                  className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy"
                  onClick={() => setEmployerDropdownOpen(false)}
                >
                  Company Profile
                </Link>
                <Link
                  href="/employer/messages"
                  className="flex items-center px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy"
                  onClick={() => setEmployerDropdownOpen(false)}
                >
                  Messages
                  {unreadCount > 0 && (
                    <span className="ml-1.5 w-[16px] h-[16px] bg-gold text-white rounded-full text-[9px] font-bold inline-flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  href="/employer/shortlist"
                  className="flex items-center px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy"
                  onClick={() => setEmployerDropdownOpen(false)}
                >
                  Shortlist
                  {shortlistCount > 0 && (
                    <span className="ml-1.5 w-[16px] h-[16px] bg-border text-text-muted rounded-full text-[9px] font-bold inline-flex items-center justify-center">
                      {shortlistCount > 99 ? "99+" : shortlistCount}
                    </span>
                  )}
                </Link>
                <Link
                  href="/employer/listings"
                  className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy"
                  onClick={() => setEmployerDropdownOpen(false)}
                >
                  Listings
                </Link>
                <Link
                  href="/browse"
                  className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy"
                  onClick={() => setEmployerDropdownOpen(false)}
                >
                  Candidates
                </Link>
                <Link
                  href="/employer/team"
                  className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy"
                  onClick={() => setEmployerDropdownOpen(false)}
                >
                  Team
                </Link>
                <div className="h-px bg-border-light mx-1 my-1.5" />
                <button
                  onClick={() => {
                    setEmployerDropdownOpen(false);
                    handleLogout();
                  }}
                  className="block w-full text-left px-3.5 py-2.5 text-[13px] font-medium text-text-muted no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-error bg-transparent border-none cursor-pointer font-sans"
                >
                  Log out
                </button>
              </div>}
            </div>
          )}

          {/* Candidate profile dropdown */}
          {personalAccount && (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                aria-expanded={profileOpen}
                aria-haspopup="true"
                aria-controls="desktop-profile-menu"
                className="flex min-h-[44px] items-center gap-2 text-[13px] font-medium text-text-body px-3.5 py-2 rounded-sm transition-all hover:text-navy hover:bg-navy/[0.04] tracking-[0.2px] bg-transparent border-none cursor-pointer font-sans"
              >
                {candidatePhotoUrl ? (
                  <Image
                    src={candidatePhotoUrl}
                    alt=""
                    width={24}
                    height={24}
                    sizes="24px"
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <span aria-hidden="true" className="w-6 h-6 rounded-full bg-navy text-white text-[10px] font-bold flex items-center justify-center">
                    {candidateName?.charAt(0).toUpperCase() ?? "?"}
                  </span>
                )}
                {candidateName ?? (companionOnly ? "Account" : "Profile")}
                {chevronSvg(profileOpen)}
              </button>
              {profileOpen && <div
                id="desktop-profile-menu"
                className="absolute top-[calc(100%+8px)] right-0 bg-white rounded-md shadow-[0_12px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.04)] p-2 min-w-[180px] transition-all duration-200 opacity-100 pointer-events-auto translate-y-0"
              >
                {accountLinks.map((link) => (
                  <Link
                    key={link.id}
                    href={link.href}
                    className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-navy"
                    onClick={() => setProfileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="h-px bg-border-light mx-1 my-1.5" />
                <button
                  onClick={() => {
                    setProfileOpen(false);
                    handleLogout();
                  }}
                  className="block w-full text-left px-3.5 py-2.5 text-[13px] font-medium text-text-muted no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-error bg-transparent border-none cursor-pointer font-sans"
                >
                  Log out
                </button>
              </div>}
            </div>
          )}

          {/* Log In + Sign up (logged out only) */}
          {!isLoggedIn && (
            <>
              <Link
                href={`/login?redirect=${encodeURIComponent(pathname)}`}
                className="inline-flex min-h-[44px] items-center whitespace-nowrap text-sm font-medium text-navy px-3 py-2 ml-2 no-underline transition-colors hover:text-gold"
              >
                Log in
              </Link>
              <Link
                href="/join"
                className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-[10px] bg-teal px-5 py-2.5 text-[15px] font-bold text-white no-underline shadow-[0_14px_28px_rgba(22,128,143,0.18)] transition-colors hover:bg-teal-dark"
              >
                Get started
              </Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        {isLoggedIn ? (
          <Link
            href={inboxPath}
            className={`${NAV_VISIBILITY_CLASSES.mobileToggle} relative ml-auto h-11 w-11 items-center justify-center rounded-sm text-text-body no-underline`}
            aria-label="Inbox"
            onClick={() => setMobileOpen(false)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            {unreadCount > 0 ? (
              <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold text-[9px] font-bold text-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            ) : null}
          </Link>
        ) : null}

        <button
          ref={mobileToggleRef}
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`${NAV_VISIBILITY_CLASSES.mobileToggle} items-center justify-center w-11 h-11 rounded-sm bg-transparent border-none cursor-pointer transition-colors hover:bg-black/[0.04] text-charcoal`}
          aria-label="Menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
        >
          {mobileOpen ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="3" y1="5" x2="17" y2="5" />
              <line x1="3" y1="10" x2="17" y2="10" />
              <line x1="3" y1="15" x2="17" y2="15" />
            </svg>
          )}
        </button>
      </nav>

      {/* Mobile Nav Panel */}
      {mobileOpen && <div
        id="mobile-navigation"
        className={`fixed top-16 left-0 right-0 bottom-0 bg-warm-bg z-[999] overflow-y-auto px-4 pb-6 pt-3 transition-all duration-250 flex flex-col opacity-100 translate-y-0 ${NAV_VISIBILITY_CLASSES.mobilePanel}`}
      >
        {!isLoggedIn && (
          <div className="mb-4 rounded-[18px] border border-line bg-white p-3 shadow-[0_12px_28px_rgba(22,35,59,0.06)]">
            <Link
              href="/join"
              className="flex min-h-[48px] items-center justify-center rounded-[12px] bg-teal px-3 py-3 text-center text-[14px] font-bold text-white no-underline shadow-[0_10px_22px_rgba(22,128,143,0.2)]"
              onClick={() => setMobileOpen(false)}
            >
              Get started
            </Link>
            <Link
              href={`/login?redirect=${encodeURIComponent(pathname)}`}
              className="mt-2 flex min-h-[44px] items-center justify-center rounded-[12px] bg-cream px-3 py-2.5 text-[14px] font-semibold text-navy no-underline"
              onClick={() => setMobileOpen(false)}
            >
              Log in
            </Link>
          </div>
        )}

        <span className="block px-1 pb-2 text-[11px] uppercase tracking-wider text-text-muted/60 font-bold">
          Main
        </span>
        <div className="grid gap-2">
          {primaryNavLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={mobilePrimaryLinkClass(link.href)}
              onClick={() => setMobileOpen(false)}
            >
              <span className="flex items-center">
                {link.label}
                {link.id === "companion" && userRole === "candidate"
                  ? companionAccountBadges.map((label) => (
                      <CompanionAccountBadge key={label} label={label} />
                    ))
                  : null}
              </span>
              <span aria-hidden="true">&rarr;</span>
            </Link>
          ))}
          <button
            onClick={() => setMobileMoreOpen(!mobileMoreOpen)}
            className="flex min-h-[54px] w-full items-center justify-between rounded-[14px] border border-line bg-white px-4 py-3 text-left font-sans text-[15px] font-semibold text-navy transition-colors active:bg-cream"
            aria-expanded={mobileMoreOpen}
            aria-controls="mobile-more-links"
          >
            More
            {chevronSvg(mobileMoreOpen)}
          </button>
        </div>

        {mobileMoreOpen && <div id="mobile-more-links" className="mt-2 rounded-[16px] border border-line bg-white py-2 shadow-[0_10px_24px_rgba(22,35,59,0.05)]">
          <span className="block px-4 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-text-muted/50 font-bold">
            More to explore
          </span>
          {SECONDARY_NAV_LINKS.map((link) => renderMobileMoreLink(link))}
          <span className="block px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-text-muted/50 font-bold">
            Resources
          </span>
          {RESOURCE_NAV_LINKS.map((link) => renderMobileMoreLink(link))}
          <span className="block px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-text-muted/50 font-bold">
            About
          </span>
          {ABOUT_NAV_LINKS.map((link) => renderMobileMoreLink(link))}
          <details className="group">
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between px-4 py-[11px] text-sm font-medium text-text-muted [&::-webkit-details-marker]:hidden">
              <span>About PeakWave</span>
              <ChevronDown
                aria-hidden="true"
                className="h-3.5 w-3.5 transition-transform group-open:rotate-180"
              />
            </summary>
            <div>
              {ABOUT_PEAKWAVE_NAV_LINKS.map((link) => renderMobileMoreLink(link, true))}
            </div>
          </details>
        </div>}

        {userRole === "employer" && (
          <>
            <span className="block px-4 pt-5 pb-1 text-[11px] uppercase tracking-wider text-text-muted/60 font-medium">
              Your Account
            </span>
            <Link
              href="/employer/dashboard"
              className={mobileNavLinkClass("/employer/dashboard")}
              onClick={() => setMobileOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              href="/employer/messages"
              className={`flex items-center ${mobileNavLinkClass("/employer/messages")}`}
              onClick={() => setMobileOpen(false)}
            >
              Messages
              {inboxBadge}
            </Link>
            <Link
              href="/employer/shortlist"
              className={`flex items-center ${mobileNavLinkClass("/employer/shortlist")}`}
              onClick={() => setMobileOpen(false)}
            >
              Shortlist
              {shortlistCount > 0 && (
                <span className="w-[18px] h-[18px] bg-border text-text-muted rounded-full text-[10px] font-bold inline-flex items-center justify-center ml-2">
                  {shortlistCount > 99 ? "99+" : shortlistCount}
                </span>
              )}
            </Link>
            <Link
              href="/employer/listings"
              className={mobileNavLinkClass("/employer/listings")}
              onClick={() => setMobileOpen(false)}
            >
              Listings
            </Link>
            <Link
              href="/employer/team"
              className={mobileNavLinkClass("/employer/team")}
              onClick={() => setMobileOpen(false)}
            >
              Team
            </Link>
            <Link
              href="/employer/company"
              className={mobileNavLinkClass("/employer/company")}
              onClick={() => setMobileOpen(false)}
            >
              Edit Profile
            </Link>
            {employerCompanySlug && (
              <Link
                href={`/company/${employerCompanySlug}`}
                className={mobileNavLinkClass(`/company/${employerCompanySlug}`)}
                onClick={() => setMobileOpen(false)}
              >
                View My Profile
              </Link>
            )}
          </>
        )}

        {personalAccount && (
          <>
            <span className="block px-4 pt-5 pb-1 text-[11px] uppercase tracking-wider text-text-muted/60 font-medium">
              Your Account
            </span>
            {accountLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className={mobileNavLinkClass(link.href)}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </>
        )}

        {/* Logged in mobile footer */}
        {isLoggedIn && (
          <div className="flex flex-col gap-2.5 px-4 pt-4">
            <button
              onClick={() => {
                setMobileOpen(false);
                handleLogout();
              }}
              className="w-full text-center px-5 py-3.5 rounded-sm text-[15px] font-semibold border-[1.5px] border-border text-text-muted bg-transparent cursor-pointer font-sans"
            >
              Log out
            </button>
          </div>
        )}
      </div>}
    </>
  );
}
